// =============================================================================
// PEDRA — src/components/Sidebar.tsx
// Coluna esquerda: tabela de pecas, tabela de chapas, painel de opcoes e a
// barra fixa de calculo no rodape (botao CALCULAR / progresso + Cancelar).
//
// Diferente dos demais componentes, a Sidebar consome `useProject()` e
// `useOptimizer()` diretamente — ela e "esperta" de proposito, para o App nao
// virar um funil de dezenas de props.
// =============================================================================

import { useCallback, useMemo, useRef, useState } from 'react';
import type {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactElement,
  ReactNode,
} from 'react';

import type { Id, Part, Project, Settings, StockSheet } from '../core/types';
import { validateProject } from '../core/validation';
import { parsePastedParts, parsePastedStock } from '../lib/paste';
import { useProject } from '../state/store';
import { useOptimizer } from '../state/useOptimizer';
import { OptionsPanel } from './OptionsPanel';
import { PartsTable } from './PartsTable';
import { StockTable } from './StockTable';

// -----------------------------------------------------------------------------
// TIPOS
// -----------------------------------------------------------------------------

export interface SidebarProps {
  /** Gaveta aberta (telas pequenas) / coluna visivel (desktop). */
  open: boolean;
  onClose(): void;
  width: number;
  onResize(width: number): void;
  onOpenSettings(): void;
  onOpenEdgeBands(): void;
  onOpenEdgeBandingFor(partId: Id): void;
  /** Dispara o calculo (o App limpa as linhas em branco antes de chamar). */
  onCalculate(): void;
  /** Peca em destaque, compartilhada com o diagrama. */
  highlightedPartId?: Id | null;
  onHoverPart?(id: Id | null): void;
}

// -----------------------------------------------------------------------------
// LINHAS EM BRANCO
//
// A `EditableGrid` mantem sempre uma linha vazia no fim de cada tabela, para o
// usuario poder digitar sem clicar em "+ Linha". Essa linha e um registro real
// do projeto, e a validacao de `core/validation` — que nao sabe da grade — a
// reprovaria por falta de medidas. Entao tudo que valida ou calcula usa o
// projeto SEM essas linhas.
// -----------------------------------------------------------------------------

/** true quando a linha de peça nunca foi preenchida. */
export function isBlankPartRow(p: Pick<Part, 'length' | 'width' | 'label'>): boolean {
  return p.length === 0 && p.width === 0 && p.label.trim() === '';
}

/** true quando a linha de chapa nunca foi preenchida. */
export function isBlankStockRow(s: Pick<StockSheet, 'length' | 'width' | 'label'>): boolean {
  return s.length === 0 && s.width === 0 && s.label.trim() === '';
}

/**
 * Devolve o projeto sem as linhas em branco automáticas. Se uma tabela ficaria
 * completamente vazia, ela e mantida como está — assim a mensagem de erro
 * continua sendo "adicione uma peça", e não "a peça sem nome está sem medida".
 */
export function projectWithoutBlankRows(project: Project): Project {
  const parts = project.parts.filter((p) => !isBlankPartRow(p));
  const stock = project.stock.filter((s) => !isBlankStockRow(s));
  const keepParts = parts.length > 0 && parts.length !== project.parts.length;
  const keepStock = stock.length > 0 && stock.length !== project.stock.length;
  if (!keepParts && !keepStock) return project;
  return {
    ...project,
    parts: keepParts ? parts : project.parts,
    stock: keepStock ? stock : project.stock,
  };
}

/** Limites de arraste da coluna — os mesmos de theme.css. */
const MIN_WIDTH = 320;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 420;
const KEY_STEP = 16;

type SectionId = 'parts' | 'stock' | 'options';

// -----------------------------------------------------------------------------
// SECAO COLAPSAVEL
// -----------------------------------------------------------------------------

interface SectionProps {
  id: SectionId;
  label: string;
  collapsed: boolean;
  onToggle(id: SectionId): void;
  children: ReactNode;
}

/**
 * Envolve um painel que ja traz o proprio cabecalho (`.grid-panel`, `.opt-panel`).
 * Recolher esconde tudo menos esse cabecalho — feito no CSS, sem desmontar o
 * componente, para nao perder selecao nem rascunho de colagem.
 */
function Section(props: SectionProps): ReactElement {
  const { id, label, collapsed, onToggle, children } = props;
  return (
    <div className={collapsed ? 'sidebar-section is-collapsed' : 'sidebar-section'}>
      <button
        type="button"
        className="sidebar-section__toggle"
        aria-expanded={!collapsed}
        title={collapsed ? `Expandir ${label}` : `Recolher ${label}`}
        onClick={() => onToggle(id)}
      >
        <span aria-hidden="true">{collapsed ? '▸' : '▾'}</span>
        <span className="sr-only">{collapsed ? `Expandir ${label}` : `Recolher ${label}`}</span>
      </button>
      {children}
    </div>
  );
}

// -----------------------------------------------------------------------------
// AVISO INTERNO DA SIDEBAR (resultado de colagem / importacao)
// -----------------------------------------------------------------------------

interface Notice {
  tone: 'info' | 'danger';
  text: string;
}

// -----------------------------------------------------------------------------
// COMPONENTE
// -----------------------------------------------------------------------------

export function Sidebar(props: SidebarProps): ReactElement {
  const {
    open,
    onClose,
    width,
    onResize,
    onOpenSettings,
    onOpenEdgeBands,
    onOpenEdgeBandingFor,
    onCalculate,
    highlightedPartId,
    onHoverPart,
  } = props;

  const { project, dispatch } = useProject();
  const { status, progress, cancel, stale, result } = useOptimizer();

  const [collapsed, setCollapsed] = useState<Readonly<Record<SectionId, boolean>>>({
    parts: false,
    stock: false,
    options: false,
  });
  const [notice, setNotice] = useState<Notice | null>(null);

  const toggleSection = useCallback((id: SectionId): void => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  // --- materiais usados no projeto (datalist das duas tabelas) ---------------
  const materials = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const p of project.parts) {
      const m = p.material.trim();
      if (m !== '') set.add(m);
    }
    for (const s of project.stock) {
      const m = s.material.trim();
      if (m !== '') set.add(m);
    }
    return [...set].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [project.parts, project.stock]);

  // --- validacao do projeto (ignorando as linhas em branco da grade) --------
  const validation = useMemo(() => validateProject(projectWithoutBlankRows(project)), [project]);

  // Projeto que o usuario ainda nem comecou a preencher. Aqui NAO existe erro
  // nenhum a relatar: as unicas linhas sao as em branco que a grade cria
  // sozinha, e reprova-las seria repreender quem acabou de abrir o app.
  const emptyState = useMemo<string | null>(() => {
    const hasPart = project.parts.some((p) => !isBlankPartRow(p));
    const hasStock = project.stock.some((s) => !isBlankStockRow(s));
    if (hasPart && hasStock) return null;
    if (!hasPart && !hasStock) {
      return 'Adicione as peças que você quer cortar e as chapas disponíveis. Depois clique em CALCULAR.';
    }
    if (!hasPart) return 'Falta informar as peças que você quer cortar.';
    return 'Falta informar a chapa de onde as peças serão cortadas.';
  }, [project.parts, project.stock]);

  const blocked = emptyState !== null || validation.errors.length > 0;

  // --- acoes das tabelas -----------------------------------------------------
  const settings: Settings = project.settings;

  const handlePartChange = useCallback(
    (id: Id, patch: Partial<Part>): void => dispatch({ type: 'updatePart', id, patch }),
    [dispatch],
  );
  const handlePartAdd = useCallback((): void => dispatch({ type: 'addPart' }), [dispatch]);
  const handlePartDelete = useCallback(
    (ids: Id[]): void => dispatch({ type: 'removeParts', ids }),
    [dispatch],
  );
  const handlePartDuplicate = useCallback(
    (ids: Id[]): void => dispatch({ type: 'duplicateParts', ids }),
    [dispatch],
  );
  const handlePartReorder = useCallback(
    (ids: Id[], toIndex: number): void => dispatch({ type: 'moveParts', ids, toIndex }),
    [dispatch],
  );
  const handlePartClear = useCallback((): void => dispatch({ type: 'clearParts' }), [dispatch]);

  const handlePartPaste = useCallback(
    (text: string): void => {
      const parsed = parsePastedParts(text, settings);
      if (parsed.rows.length === 0) {
        setNotice({
          tone: 'danger',
          text:
            parsed.errors[0] ??
            'Nada foi reconhecido no texto colado. Use uma coluna por medida: comprimento, largura, quantidade.',
        });
        return;
      }
      dispatch({ type: 'addParts', parts: parsed.rows });
      const extra =
        parsed.skipped > 0
          ? ` · ${parsed.skipped} ${parsed.skipped === 1 ? 'linha ignorada' : 'linhas ignoradas'}`
          : '';
      setNotice({
        tone: parsed.errors.length > 0 ? 'danger' : 'info',
        text: `${parsed.rows.length} ${parsed.rows.length === 1 ? 'peça adicionada' : 'peças adicionadas'}${extra}.`,
      });
    },
    [dispatch, settings],
  );

  const handleStockChange = useCallback(
    (id: Id, patch: Partial<StockSheet>): void => dispatch({ type: 'updateStock', id, patch }),
    [dispatch],
  );
  const handleStockAdd = useCallback((): void => dispatch({ type: 'addStock' }), [dispatch]);
  const handleStockDelete = useCallback(
    (ids: Id[]): void => dispatch({ type: 'removeStock', ids }),
    [dispatch],
  );
  const handleStockDuplicate = useCallback(
    (ids: Id[]): void => dispatch({ type: 'duplicateStock', ids }),
    [dispatch],
  );
  const handleStockReorder = useCallback(
    (ids: Id[], toIndex: number): void => dispatch({ type: 'moveStock', ids, toIndex }),
    [dispatch],
  );
  const handleStockClear = useCallback((): void => dispatch({ type: 'clearStock' }), [dispatch]);

  const handleStockPaste = useCallback(
    (text: string): void => {
      const parsed = parsePastedStock(text, settings);
      if (parsed.rows.length === 0) {
        setNotice({
          tone: 'danger',
          text:
            parsed.errors[0] ??
            'Nada foi reconhecido no texto colado. Use uma coluna por medida: comprimento, largura, quantidade.',
        });
        return;
      }
      dispatch({ type: 'addStockSheets', sheets: parsed.rows });
      setNotice({
        tone: parsed.errors.length > 0 ? 'danger' : 'info',
        text: `${parsed.rows.length} ${parsed.rows.length === 1 ? 'chapa adicionada' : 'chapas adicionadas'}.`,
      });
    },
    [dispatch, settings],
  );

  const handleSettingsChange = useCallback(
    (patch: Partial<Settings>): void => dispatch({ type: 'updateSettings', patch }),
    [dispatch],
  );

  // --- chapas efetivamente usadas no ultimo calculo --------------------------
  const usedStockIds = useMemo<Id[]>(() => {
    if (!result) return [];
    const set = new Set<Id>();
    for (const s of result.sheets) set.add(s.stockSheetId);
    return [...set];
  }, [result]);

  // --- redimensionamento -----------------------------------------------------
  const draggingRef = useRef(false);

  const clampWidth = (value: number): number =>
    Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.round(value)));

  const handleResizePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>): void => {
      if (e.button !== 0) return;
      draggingRef.current = true;
      const target = e.currentTarget;
      target.setPointerCapture(e.pointerId);
      e.preventDefault();
    },
    [],
  );

  const handleResizePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>): void => {
      if (!draggingRef.current) return;
      onResize(clampWidth(e.clientX));
    },
    [onResize],
  );

  const handleResizePointerUp = useCallback((e: ReactPointerEvent<HTMLDivElement>): void => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    const target = e.currentTarget;
    if (target.hasPointerCapture(e.pointerId)) target.releasePointerCapture(e.pointerId);
  }, []);

  const handleResizeKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>): void => {
      let next: number | null = null;
      if (e.key === 'ArrowLeft') next = width - KEY_STEP;
      else if (e.key === 'ArrowRight') next = width + KEY_STEP;
      else if (e.key === 'Home') next = MIN_WIDTH;
      else if (e.key === 'End') next = MAX_WIDTH;
      if (next === null) return;
      e.preventDefault();
      onResize(clampWidth(next));
    },
    [width, onResize],
  );

  // --- barra de calculo ------------------------------------------------------
  const running = status === 'running';
  const percent = progress ? Math.max(0, Math.min(100, progress.percent)) : 0;

  const calcTitle =
    emptyState !== null
      ? emptyState
      : validation.errors.length > 0
        ? `Ajuste ${validation.errors.length === 1 ? 'o item pendente' : `os ${validation.errors.length} itens pendentes`} para calcular`
        : 'Calcular o plano de corte (Ctrl+Enter)';

  return (
    <aside
      id="pedra-sidebar"
      className={open ? 'sidebar-root app-sidebar is-open' : 'sidebar-root app-sidebar'}
      aria-label="Entradas do projeto"
      aria-hidden={!open}
    >
      <div className="sidebar-head">
        <h2 className="sidebar-head__title">Entradas</h2>
        <button
          type="button"
          className="icon-btn sidebar-head__close"
          onClick={onClose}
          title="Fechar as entradas (Ctrl+B)"
        >
          <span aria-hidden="true">✕</span>
          <span className="sr-only">Fechar as entradas</span>
        </button>
      </div>

      <div className="sidebar-body">
        {notice !== null ? (
          <div
            className={
              notice.tone === 'danger'
                ? 'notice notice--danger sidebar-notice'
                : 'notice notice--info sidebar-notice'
            }
            role="status"
          >
            <span className="truncate">{notice.text}</span>
            <button
              type="button"
              className="icon-btn"
              onClick={() => setNotice(null)}
              title="Fechar aviso"
            >
              <span aria-hidden="true">✕</span>
              <span className="sr-only">Fechar aviso</span>
            </button>
          </div>
        ) : null}

        <Section id="parts" label="Peças" collapsed={collapsed.parts} onToggle={toggleSection}>
          <PartsTable
            parts={project.parts}
            settings={settings}
            edgeBands={project.edgeBands}
            materials={materials}
            onChange={handlePartChange}
            onAdd={handlePartAdd}
            onDelete={handlePartDelete}
            onDuplicate={handlePartDuplicate}
            onPaste={handlePartPaste}
            onReorder={handlePartReorder}
            onClear={handlePartClear}
            onOpenEdgeBanding={onOpenEdgeBandingFor}
            highlightedPartId={highlightedPartId ?? null}
            onHoverPart={onHoverPart}
          />
        </Section>

        <Section id="stock" label="Chapas" collapsed={collapsed.stock} onToggle={toggleSection}>
          <StockTable
            stock={project.stock}
            settings={settings}
            materials={materials}
            onChange={handleStockChange}
            onAdd={handleStockAdd}
            onDelete={handleStockDelete}
            onDuplicate={handleStockDuplicate}
            onPaste={handleStockPaste}
            onReorder={handleStockReorder}
            onClear={handleStockClear}
            usedStockIds={usedStockIds}
          />
        </Section>

        <Section id="options" label="Opções" collapsed={collapsed.options} onToggle={toggleSection}>
          <OptionsPanel
            settings={settings}
            onChange={handleSettingsChange}
            onOpenSettings={onOpenSettings}
            onOpenEdgeBands={onOpenEdgeBands}
          />
        </Section>
      </div>

      <div className="sidebar-calc">
        {emptyState !== null ? (
          <p className="sidebar-calc__stale">{emptyState}</p>
        ) : validation.errors.length > 0 ? (
          <div className="sidebar-calc__errors notice notice--danger" role="alert">
            <strong className="sidebar-calc__errors-title">
              {validation.errors.length === 1
                ? 'Falta um ajuste para calcular:'
                : `Faltam ${validation.errors.length} ajustes para calcular:`}
            </strong>
            <ul className="sidebar-calc__list">
              {validation.errors.slice(0, 4).map((msg) => (
                <li key={msg}>{msg}</li>
              ))}
              {validation.errors.length > 4 ? (
                <li>{`e mais ${validation.errors.length - 4}…`}</li>
              ) : null}
            </ul>
          </div>
        ) : validation.warnings.length > 0 ? (
          <div className="sidebar-calc__errors notice notice--warning" role="status">
            <strong className="sidebar-calc__errors-title">
              Dá para calcular assim mesmo. Confira:
            </strong>
            <ul className="sidebar-calc__list">
              {validation.warnings.slice(0, 2).map((msg) => (
                <li key={msg}>{msg}</li>
              ))}
              {validation.warnings.length > 2 ? (
                <li>{`e mais ${validation.warnings.length - 2}…`}</li>
              ) : null}
            </ul>
          </div>
        ) : stale ? (
          <p className="sidebar-calc__stale">
            O projeto mudou depois do último cálculo. Calcule de novo para atualizar o plano.
          </p>
        ) : null}

        {running ? (
          <div className="sidebar-calc__running">
            <div className="progress sidebar-calc__progress">
              <div className="progress__bar" style={{ width: `${percent}%` }} />
            </div>
            <button type="button" className="btn btn--danger btn--block" onClick={cancel}>
              Cancelar cálculo
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="btn btn--primary btn--block sidebar-calc__button"
            onClick={onCalculate}
            disabled={blocked}
            title={calcTitle}
          >
            <span aria-hidden="true">▶</span> CALCULAR
            <kbd className="sidebar-calc__kbd">Ctrl+Enter</kbd>
          </button>
        )}
      </div>

      <div
        className="sidebar-resizer"
        role="separator"
        aria-orientation="vertical"
        aria-label="Ajustar a largura das entradas"
        aria-valuenow={width}
        aria-valuemin={MIN_WIDTH}
        aria-valuemax={MAX_WIDTH}
        tabIndex={0}
        onPointerDown={handleResizePointerDown}
        onPointerMove={handleResizePointerMove}
        onPointerUp={handleResizePointerUp}
        onPointerCancel={handleResizePointerUp}
        onKeyDown={handleResizeKeyDown}
        onDoubleClick={() => onResize(DEFAULT_WIDTH)}
        title="Arraste para ajustar a largura · duplo clique restaura o padrão"
      />
    </aside>
  );
}

export default Sidebar;
